import { useRef, useCallback, useEffect } from "react";
import Reveal from "./Reveal";
import * as Icons from "lucide-react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const getSlug = (title) => 
  title.toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');

export default function ServicesHub({ services, sectionContent, uiStrings, globals, elementVisibility }) {
  const items = services && services.length > 0 ? services : [];
  const loopItems = [...items, ...items, ...items, ...items];
  const trackRef = useRef(null);
  const offsetRef = useRef(0);
  const targetRef = useRef(0);
  const rafRef = useRef(null);
  const intervalRef = useRef(null);
  const isPaused = useRef(false);
  const hasStarted = useRef(false);

  useEffect(() => {
    const el = trackRef.current;
    if (!el || items.length === 0) return;

    const SET_WIDTH = 25;
    const CARD_WIDTH = 25 / items.length;
    const AUTO_SPEED = SET_WIDTH / 1800;
    let lastTime = 0;

    const tick = (time) => {
      if (lastTime === 0) lastTime = time;
      const delta = (time - lastTime) / 16.67;
      lastTime = time;

      if (!isPaused.current) {
        targetRef.current -= AUTO_SPEED * delta;
      }

      offsetRef.current += (targetRef.current - offsetRef.current) * 0.1;

      if (offsetRef.current <= -SET_WIDTH) {
        offsetRef.current += SET_WIDTH;
        targetRef.current += SET_WIDTH;
      }
      if (offsetRef.current > 0) {
        offsetRef.current -= SET_WIDTH;
        targetRef.current -= SET_WIDTH;
      }

      el.style.transform = `translateX(${offsetRef.current}%)`;
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [items.length]);

  const startRepeat = useCallback((dir) => {
    isPaused.current = true;
    const CARD_WIDTH = 25 / items.length;
    targetRef.current -= dir * CARD_WIDTH;
    intervalRef.current = setInterval(() => {
      targetRef.current -= dir * CARD_WIDTH;
    }, 800);
  }, [items.length]);

  const stopRepeat = useCallback(() => {
    clearInterval(intervalRef.current);
    isPaused.current = false;
  }, []);

  const eyebrow = sectionContent?.eyebrow || "Hub Profissional";
  const headline = sectionContent?.headline || "Um único ecossistema para o seu próximo capítulo.";
  const description = sectionContent?.description || "Quatro frentes que se conectam para te levar adiante — patrimônio, estratégia e estética em sintonia. Discrição, técnica e refinamento em cada decisão.";
  const learnMoreLabel = uiStrings?.learn_more || "Saber mais";

  return (
    <section
      id="servicos"
      data-testid="services-section"
      className="relative bg-offwhite py-28 md:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-20">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow text-warmgray">{eyebrow}</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-softblack mt-4 md:mt-6 leading-[1.05]">
              {headline}
            </h2>
          </Reveal>
          <Reveal delay={200} className="lg:col-span-6 lg:col-start-7 lg:pt-6">
            <p className="text-warmgray text-sm sm:text-base md:text-lg leading-relaxed font-light">
              {description}
            </p>
          </Reveal>
        </div>

        {/* Mobile: vertical stack */}
        {elementVisibility?.services_carousel !== false && (
        <div className="md:hidden grid gap-4">
          {items.map((s, i) => {
            const IconComponent = Icons[s.icon_name] || Icons.HelpCircle;
            const slug = getSlug(s.title);
            return (
              <div
                key={s.id}
                className="group p-6 border border-fendi/70 transition-colors duration-700 hover:bg-fendi/20"
              >
                <a href={`#${slug}`} className="block">
                  <div className="flex items-start justify-between">
                    <span className="eyebrow text-warmgray">{String(i + 1).padStart(2, '0')}</span>
                    <IconComponent size={20} strokeWidth={1} className="text-warmgray" />
                  </div>
                  <h3 className="font-serif text-xl text-softblack mt-4 leading-tight">
                    {s.title}
                  </h3>
                  <p className="text-warmgray text-sm leading-relaxed mt-3 font-light line-clamp-3 overflow-hidden">
                    {s.description}
                  </p>
                  <div className="mt-5 inline-flex items-center gap-3 text-softblack">
                    <span className="text-[11px] tracking-[0.28em] uppercase font-medium">
                      {learnMoreLabel}
                    </span>
                    <span className="block w-6 h-px bg-softblack group-hover:w-10 group-hover:bg-champagne transition-all duration-500" />
                  </div>
                </a>
              </div>
            );
          })}
        </div>
        )}

        {/* Desktop: infinite smooth horizontal scroll */}
        {elementVisibility?.services_carousel !== false && (
        <div
          className="hidden md:block overflow-hidden border-t border-fendi/70 relative group/carousel"
          onMouseEnter={() => { isPaused.current = true; }}
          onMouseLeave={() => { isPaused.current = false; }}
        >
          <div
            ref={trackRef}
            className="flex"
            style={{ width: `${items.length * 100}%`, willChange: 'transform' }}
          >
            {loopItems.map((s, i) => {
              const IconComponent = Icons[s.icon_name] || Icons.HelpCircle;
              const slug = getSlug(s.title);
              return (
                <div
                  key={`${s.id}-${i}`}
                  className="group/card relative p-12 border-b border-fendi/70 border-r border-fendi/70 transition-colors duration-700 hover:bg-fendi/20"
                  style={{ flex: `0 0 ${25 / items.length}%`, height: "520px" }}
                >
                  <a href={`#${slug}`} className="block h-full flex flex-col">
                    <div className="flex items-start justify-between shrink-0">
                      <span className="eyebrow text-warmgray">{String((i % items.length) + 1).padStart(2, '0')}</span>
                      <IconComponent
                        size={22}
                        strokeWidth={1}
                        className="text-warmgray group-hover/card:text-champagne transition-colors duration-500"
                      />
                    </div>
                    <h3 className="font-serif text-[34px] text-softblack mt-12 leading-tight shrink-0">
                      {s.title}
                    </h3>
                    <p className="text-warmgray text-[15px] leading-relaxed mt-6 font-light line-clamp-3 overflow-hidden">
                      {s.description}
                    </p>
                    <div className="mt-auto inline-flex items-center gap-3 text-softblack">
                      <span className="text-[11px] tracking-[0.28em] uppercase font-medium">
                        {learnMoreLabel}
                      </span>
                      <span className="block w-8 h-px bg-softblack group-hover/card:w-14 group-hover/card:bg-champagne transition-all duration-500" />
                    </div>
                  </a>
                </div>
              );
            })}
          </div>

          {/* Nav buttons */}
          <button
            type="button"
            onMouseDown={() => startRepeat(-1)}
            onMouseUp={stopRepeat}
            onMouseLeave={stopRepeat}
            aria-label="Anterior"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-offwhite/90 border border-fendi/40 text-softblack opacity-0 group-hover/carousel:opacity-100 hover:bg-offwhite hover:border-champagne transition-all duration-300 shadow-md"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            type="button"
            onMouseDown={() => startRepeat(1)}
            onMouseUp={stopRepeat}
            onMouseLeave={stopRepeat}
            aria-label="Próximo"
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-offwhite/90 border border-fendi/40 text-softblack opacity-0 group-hover/carousel:opacity-100 hover:bg-offwhite hover:border-champagne transition-all duration-300 shadow-md"
          >
            <ChevronRight size={20} />
          </button>
        </div>
        )}
      </div>

      {/* Detail sections per service */}
      {elementVisibility?.services_details !== false && items.map((s, i) => (
        <ServiceDetail 
          key={s.id} 
          service={s} 
          reverse={i % 2 === 1} 
          ctaLabel={globals?.cta_label || "Agendar Consultoria"} 
        />
      ))}
    </section>
  );
}

function ServiceDetail({ service, reverse, ctaLabel }) {
  const slug = getSlug(service.title);
  return (
    <div
      id={slug}
      data-testid={`service-detail-${service.id}`}
      className="max-w-7xl mx-auto px-6 md:px-12 mt-20 md:mt-32 first-of-type:mt-20 md:first-of-type:mt-32 scroll-mt-24"
    >
      <div className={`grid lg:grid-cols-12 gap-12 items-center ${reverse ? "lg:[direction:rtl]" : ""}`}>
        <Reveal className="lg:col-span-7 [direction:ltr]">
          <div className="relative overflow-hidden">
            <img
              src={service.image_url}
              alt={service.title}
              className="w-full h-[240px] sm:h-[320px] md:h-[560px] object-cover transform transition-transform duration-[1800ms] hover:scale-105"
              loading="lazy"
            />
          </div>
        </Reveal>
        <Reveal delay={150} className="lg:col-span-5 [direction:ltr]">
          <p className="eyebrow text-warmgray">{service.title}</p>
          <h3 className="font-serif text-3xl sm:text-4xl md:text-5xl text-softblack mt-4 md:mt-6 leading-[1.05]">
            {service.title}
          </h3>
          <p className="text-warmgray text-sm md:text-base leading-relaxed mt-4 md:mt-6 font-light">
            {service.description}
          </p>
          <ul className="mt-6 md:mt-8 space-y-3 md:space-y-4">
            {service.bullets?.map((b) => (
              <li key={b} className="flex items-baseline gap-3 md:gap-4">
                <span className="block w-5 md:w-6 h-px bg-champagne mt-2 shrink-0" />
                <span className="text-softblack/85 text-sm md:text-[15px] font-light">{b}</span>
              </li>
            ))}
          </ul>
          <a href="#contato" className="btn-ghost mt-8 md:mt-10" data-testid={`service-cta-${service.id}`}>
            {ctaLabel}
          </a>
        </Reveal>
      </div>
    </div>
  );
}
